import { CheckCircle, Help, QuestionMark, RemoveCircle } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { Typography, styled } from '@mui/material';
import moment from 'moment';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { Dialog, FormButton, Table, TablePagination, TableSort, theme } from 'thermia-react-container';
import useLeadsService from '../service/useLeadsService';
import { LeadConnectionStatusTypeEnum, LeadItem, LeadsState, TagsClosedEnum } from '../types/types';
import LeadsDialog from './leadsDialog';

type CompletedLeadsProps = {
  leadsStatus: LeadsState;
};

const LeadsTable: FC<CompletedLeadsProps> = ({ leadsStatus }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('leads');
  const navigate = useNavigate();
  const [selectedLead, setSelectedLead] = useState<LeadItem>();

  const { getLeadsList } = useLeadsService();

  const fetchData = async (
    pagination: TablePagination | null,
    search: string | null,
    sort: TableSort | null
  ): Promise<{ data: any[]; totalCount: number }> => {
    let response = await getLeadsList(leadsStatus, pagination!.skip, pagination!.take);

    // Filter items based on tagsClosed type for active and handled modes
    if(leadsStatus === LeadsState.active) {
      response.items = response.items.filter((item: LeadItem) => item.tagsClosed.type === TagsClosedEnum.None || item.tagsClosed.type === TagsClosedEnum.Unsure);
    }
    else if(leadsStatus === LeadsState.handled) {
      response.items = response.items.filter((item: LeadItem) => item.tagsClosed.type === TagsClosedEnum.Deal || item.tagsClosed.type === TagsClosedEnum.NoDeal);
    }
    
    const items = response.items.map((lead) => ({
      qualifyDate: moment(lead.qualifyDate, moment.ISO_8601).format('YYYY-MM-DD'),
      source: lead.source ?? '',
      name: lead.name ?? '',
      type: lead.type ?? '',
      city: lead.city ?? '',
      leadStatus: lead.leadStatus ?? { text: '', value: '' },
      tagsClosed: lead.tagsClosed ?? { text: '', value: '' },
      id: lead.id ?? ''
    }));
    return {
      data: items,
      totalCount: response.totalCount
    };
  };

  const onClick = (value: LeadItem) => {
    navigate(`/leads/${value.id}`);
  };

  const tableMethods = {
    refreshData: () => Promise.resolve() // function will be set by the table
  };

  const refreshTableData = async () => {
    await tableMethods.refreshData();
  };

  function getHandledIcon(data: LeadConnectionStatusTypeEnum) {
    switch (data) {
      case LeadConnectionStatusTypeEnum.customerContact:
        return <LocalPhoneIcon style={{ marginRight: 5 }} />;
      case LeadConnectionStatusTypeEnum.sentToReseller:
        return <EmailIcon style={{ marginRight: 5 }} />;
      case LeadConnectionStatusTypeEnum.newTryWithCustomer:
        return <ForwardToInboxIcon style={{ marginRight: 5 }} />;
      case LeadConnectionStatusTypeEnum.customerNotReachable:
        return <ReportGmailerrorredIcon style={{ marginRight: 5 }} />;
      default:
        return null;
    }
  }

  function getTagsClosedIcon(data: TagsClosedEnum) {
    switch (data) {
      case TagsClosedEnum.Deal:
        return <CheckCircle style={{ marginRight: 5, color: theme.palette.success.main }} />;
      case TagsClosedEnum.Unsure:
        return <Help style={{ marginRight: 5, color: theme.palette.warning.main }} />;
      case TagsClosedEnum.NoDeal:
        return <RemoveCircle style={{ marginRight: 5, color: theme.palette.error.main }} />;
      case TagsClosedEnum.None:
        return <QuestionMark style={{ marginRight: 5, color: theme.palette.primary.light }} />;
      default:
        return null;
    }
  }

  const columns: Column<LeadItem>[] = useMemo(
    () => [
      {
        accessor: 'qualifyDate',
        Header: t('leads.general.qualifyDate'),
        Cell: (prop) => (
          <>{prop.row.original.qualifyDate !== '' ? moment(prop.row.original.qualifyDate, moment.ISO_8601).format('YYYY-MM-DD') : '-'}</>
        )
      },
      {
        accessor: 'name',
        Header: t('leads.general.leadName'),
        Cell: (prop) => (
          <>
            {prop.row.original.name!.split(',').map((leadName, index) => (
              <StyledContainer key={`${prop.row.id}-${leadName}-${index}`}>
                <span>{leadName}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'city',
        Header: t('leads.general.city'),
        Cell: (prop) => (
          <>
            <StyledContainer>
              <p>{prop.row.original.city}</p>
            </StyledContainer>
          </>
        )
      },
      {
        accessor: 'source',
        Header: t('leads.general.source'),
        Cell: (prop) => (
          <>
            <StyledContainer>
              <p>{prop.row.original.source}</p>
            </StyledContainer>
          </>
        )
      },
      {
        accessor: 'type',
        Header: t('leads.general.type'),
        Cell: (prop) => (
          <>
            <StyledContainer>
              <p>{prop.row.original.type}</p>
            </StyledContainer>
          </>
        )
      },
      {
        accessor: 'leadStatus',
        Header: t('leads.general.status'),
        Cell: (prop) =>
          (prop.row.original.leadStatus.type !== LeadConnectionStatusTypeEnum.closed && (
            <>
              <StyledContainer>
                {getHandledIcon(prop.row.original.leadStatus.type)}
                <p>{prop.row.original.leadStatus.text}</p>
              </StyledContainer>
            </>
          )) ||
          (prop.row.original.leadStatus.type === LeadConnectionStatusTypeEnum.closed && (
            <>
              <StyledContainer>
                {getTagsClosedIcon(prop.row.original.tagsClosed.type)}
                <p style={prop.row.original.tagsClosed.type === TagsClosedEnum.None ? { fontStyle: 'italic' } : undefined}>
                  {prop.row.original.tagsClosed.text}
                </p>
              </StyledContainer>
            </>
          ))
      },

      {
        accessor: 'id',
        id: 'menu',
        Header: t('leads.general.handleAction'),
        Cell: (prop) => {
          return (
            <>
              <FormButton
                stopPropagation={true}
                onClick={() => {
                  setSelectedLead(prop.row.original);
                  setOpen(true);
                }}
                style={{ backgroundColor: theme.palette.primary.light, borderRadius: '7px', height: 35 }}
                variant="contained"
                title={t('leads.general.leadsHandleMenu')}
              />
            </>
          );
        }
      }
    ],
    [t]
  );

  return (
    <>
      <Dialog id={'leads-modal'} open={open}>
        <LeadsDialog refreshTableData={refreshTableData} selectedLead={selectedLead!} handleCancel={() => setOpen(false)} leadsStatus={leadsStatus} />
      </Dialog>
      <Table
        columns={columns}
        fetchData={fetchData}
        onClick={onClick}
        pagination={true}
        exposedMethods={tableMethods}
        emptyDataElement={
          <Typography component="div" gutterBottom style={{ textAlign: 'center', marginBottom: 0 }}>
            {t('leads.general.noLeads')}
          </Typography>
        }
      />
    </>
  );
};

export default LeadsTable;

const StyledContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '36px'
}));
